import React, { useCallback, useEffect, useState } from "react";
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Select,
    Space,
    message,
    Popconfirm,
    Avatar,
} from "antd";
import { PlusOutlined, FileImageOutlined, ReloadOutlined } from "@ant-design/icons";
import { api } from "../api";
import { fmtDate } from "../utils";
import ImageUpload from "../components/ImageUpload";

const PostsPage = () => {
    const [items, setItems] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [preview, setPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    const groupNameOf = (g) =>
        g && typeof g === "object" ? g.name : g || "—";

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.listPosts();
            setItems(res.items);
        } catch (e) {
            message.error(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        api.listGroups()
            .then((r) => setGroups(r.items))
            .catch(() => {});
    }, [load]);

    const openEdit = (p) => {
        setEditing(p);
        form.setFieldsValue({
            ...p,
            // authorGroup is populated to { _id, name } — the form needs the id.
            authorGroup:
                p.authorGroup && typeof p.authorGroup === "object"
                    ? p.authorGroup._id
                    : p.authorGroup,
        });
    };
    const openCreate = () => {
        setEditing({});
        form.resetFields();
    };

    const save = async () => {
        const v = await form.validateFields();
        setSaving(true);
        try {
            if (editing._id) {
                await api.updatePost(editing._id, v);
                message.success("Post updated");
            } else {
                await api.createPost(v);
                message.success("Post published");
            }
            setEditing(null);
            load();
        } catch (e) {
            message.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        try {
            await api.deletePost(id);
            message.success("Deleted");
            load();
        } catch (e) {
            message.error(e.message);
        }
    };

    const columns = [
        {
            title: "Title",
            key: "title",
            render: (_, r) => (
                <Space>
                    <Avatar shape="square" src={r.image} icon={<FileImageOutlined />} />
                    <span style={{ fontWeight: 600 }}>{r.title}</span>
                </Space>
            ),
        },
        {
            title: "Content",
            dataIndex: "content",
            key: "content",
            render: (c) =>
                c ? (
                    <span className="admin-cell-sub" style={{ color: "#475569" }}>
                        {c.slice(0, 50)}
                        {c.length > 50 ? "…" : ""}
                    </span>
                ) : (
                    "—"
                ),
        },
        {
            title: "Author group",
            dataIndex: "authorGroup",
            key: "authorGroup",
            render: groupNameOf,
        },
        {
            title: "Likes",
            dataIndex: "likes",
            key: "likes",
            width: 80,
            render: (l) => l?.length || 0,
        },
        {
            title: "Published",
            dataIndex: "createdAt",
            key: "createdAt",
            width: 160,
            defaultSortOrder: "descend",
            sorter: (a, b) =>
                new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
            render: (d) => fmtDate(d, true),
        },
        {
            title: "Actions",
            key: "actions",
            width: 160,
            render: (_, r) => (
                <Space>
                    <Button size="small" onClick={() => openEdit(r)}>
                        Edit
                    </Button>
                    <Popconfirm title="Delete?" onConfirm={() => remove(r._id)}>
                        <Button size="small" danger>
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div className="admin-page-head">
                <h1 className="admin-page-title">Posts</h1>
                <Space>
                    <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>
                        Refresh
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                        New post
                    </Button>
                </Space>
            </div>

            <Table
                rowKey="_id"
                loading={loading}
                columns={columns}
                dataSource={items}
                scroll={{ x: "max-content" }}
                onRow={(record) => ({
                    className: "admin-row-clickable",
                    onClick: (e) => {
                        if (e.target.closest("button")) return;
                        setPreview(record);
                    },
                })}
            />

            {/* Preview */}
            <Modal
                open={!!preview}
                title={preview ? preview.title : ""}
                onCancel={() => setPreview(null)}
                footer={null}
                width={680}
            >
                {preview && (
                    <>
                        {preview.image && (
                            <img
                                src={preview.image}
                                alt={preview.title}
                                style={{
                                    width: "100%",
                                    borderRadius: 8,
                                    marginBottom: 12,
                                }}
                            />
                        )}
                        <span className="admin-cell-sub">
                            {groupNameOf(preview.authorGroup)} ·{" "}
                            {(preview.likes || []).length} likes ·{" "}
                            {fmtDate(preview.createdAt, true)}
                        </span>
                        <p style={{ whiteSpace: "pre-wrap", marginTop: 12 }}>
                            {preview.content || "No content."}
                        </p>
                    </>
                )}
            </Modal>

            <Modal
                open={!!editing}
                title={editing && editing._id ? "Edit post" : "Publish post"}
                onCancel={() => setEditing(null)}
                onOk={save}
                confirmLoading={saving}
                width={600}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="title" label="Title" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="content" label="Content">
                        <Input.TextArea rows={6} />
                    </Form.Item>
                    <Form.Item name="authorGroup" label="Author group">
                        <Select
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder="Select a group"
                            options={groups.map((g) => ({
                                value: g._id,
                                label: g.name,
                            }))}
                        />
                    </Form.Item>
                    <Form.Item name="image" label="Image">
                        <ImageUpload />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default PostsPage;
